"use client";

import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo, useCallback } from "react";
import { FestivalPoster, type FestivalPosterProps } from "./festival-poster";

export interface ClickableFestivalPosterProps extends FestivalPosterProps {
	href: string | null;
	onClick?: (e: React.MouseEvent) => void;
	trackingData?: {
		festivalId: string;
		festivalName: string;
		source: "artist" | "search" | "discover" | "profile";
	};
	ariaLabel?: string;
}

/**
 * Clickable wrapper for FestivalPoster that provides consistent hover effects
 * and navigation behavior across the application.
 *
 * Features:
 * - Standardized hover effects (scale, ring)
 * - Active state feedback
 * - Keyboard navigation support
 * - Analytics tracking
 * - Accessibility features
 */
export const ClickableFestivalPoster = memo<ClickableFestivalPosterProps>(
	function ClickableFestivalPoster({
		href,
		onClick,
		trackingData,
		ariaLabel,
		className,
		// Pass through all FestivalPoster props
		name,
		posterUrl,
		size = "lg",
		priority = false,
		rounded = "lg",
		...otherProps
	}) {
		const handleClick = useCallback(
			(e: React.MouseEvent) => {
				e.stopPropagation(); // Prevent parent click handlers

				// Track festival discovery if data provided
				if (trackingData) {
					// Note: We can add festival tracking analytics here if needed
				}

				// Call custom onClick if provided
				onClick?.(e);
			},
			[onClick, trackingData],
		);

		const festivalPosterComponent = (
			<FestivalPoster
				name={name}
				posterUrl={posterUrl}
				size={size}
				priority={priority}
				className={className}
				rounded={rounded}
				{...otherProps}
			/>
		);

		// If no href provided, just return the poster
		if (!href) {
			return <div className="flex-shrink-0">{festivalPosterComponent}</div>;
		}

		// Wrap in Link with standardized hover effects
		return (
			<div className="flex-shrink-0">
				<Link
					href={href}
					className={cn(
						"group block outline-none",
						"rounded-lg", // Festival posters are always rectangular
						"focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2",
					)}
					aria-label={ariaLabel || `View ${name} festival`}
					onClick={handleClick}
				>
					<div
						className={cn(
							"overflow-hidden transition-all duration-200",
							"rounded-lg", // Festival posters are always rectangular
							"group-hover:ring-4 group-hover:ring-primary/10",
							"group-active:scale-95 group-active:ring-primary/20",
						)}
					>
						<div className="transition-transform duration-200 group-hover:scale-105 group-active:scale-100">
							{festivalPosterComponent}
						</div>
					</div>
				</Link>
			</div>
		);
	},
);

ClickableFestivalPoster.displayName = "ClickableFestivalPoster";
